import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  AgendaDataService,
  EstabelecimentoDataService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import {
  IAgendamento,
  StatusAgendamentoEnum,
} from '@nomoh-monorepo/shared-types';
import { formatarTelefone as aplicarMascaraTelefone } from '@nomoh-monorepo/shared-util-tools';
import { DateTime } from 'luxon';
import { DataViewModule } from 'primeng/dataview';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  imports: [DataViewModule, CommonModule, TranslateModule],
  selector: 'lib-agenda-notificacao',
  standalone: true,
  templateUrl: './agenda-notificacao.component.html',
  styleUrls: ['./agenda-notificacao.component.scss'],
})
export class AgendaNotificacaoComponent {
  private readonly timeZonePadrao = 'America/Sao_Paulo';

  @Output() itemClicado = new EventEmitter<void>();
  @Input() numberRows = 3;
  @Input() showEmptyState = false;

  private _agendamentos = signal<IAgendamento[]>([]);
  $agendamentos = this._agendamentos.asReadonly();
  setAgendamentos(agendamentos: IAgendamento[]) {
    this._agendamentos.set(agendamentos);
  }

  private readonly agendaDataService = inject(AgendaDataService);
  private readonly estabelecimentoDataService = inject(EstabelecimentoDataService);
  private readonly router = inject(Router);
  constructor() {
    effect(
      () => {
        const agendamentos =
          this.agendaDataService.$agendaInDb().agendamentos ?? [];
        const proximosAgendamentos =
          this.filtrarAgendamentosAgendados(agendamentos);

        this.setAgendamentos(proximosAgendamentos);
        this.agendaDataService.setAtualizarContadorAgendados(
          proximosAgendamentos.length
        );
      }
    );
  }

  formatarHora(data: Date | string) {
    const dataFormatada = this.parseDateTime(data);
    return dataFormatada ? dataFormatada.toFormat('HH:mm') : '';
  }

  formatarDataCurta(data: Date | string) {
    const dataFormatada = this.parseDateTime(data);
    return dataFormatada ? dataFormatada.toFormat('dd/MM') : '';
  }

  formatarTelefone(telefone?: string | null) {
    return telefone ? aplicarMascaraTelefone(telefone) : '';
  }

  acessarModalInformacao(agendamento: IAgendamento) {
    this.agendaDataService.setAgendamentoParaModal(agendamento);
    this.itemClicado.emit();
  }

  private filtrarAgendamentosAgendados(
    agendamentos: IAgendamento[]
  ): IAgendamento[] {
    const agora = DateTime.now().setZone(this.getTimeZoneEstabelecimento());
    const statusElegiveis = new Set<StatusAgendamentoEnum>([
      StatusAgendamentoEnum.PENDENTE,
    ]);

    return agendamentos.filter((agendamento) => {
      const status = Number(agendamento.status);
      if (!statusElegiveis.has(status as StatusAgendamentoEnum)) {
        return false;
      }

      const dataHora = this.parseDateTime(agendamento.dataHora);
      if (!dataHora) {
        return false;
      }

      return dataHora >= agora;
    });
  }

  private parseDateTime(
    valor: Date | string | null | undefined
  ): DateTime | null {
    if (!valor) {
      return null;
    }

    const data =
      typeof valor === 'string'
        ? DateTime.fromISO(valor).setZone(this.getTimeZoneEstabelecimento())
        : DateTime.fromJSDate(valor).setZone(this.getTimeZoneEstabelecimento());

    return data.isValid ? data : null;
  }

  private getTimeZoneEstabelecimento(): string {
    const timeZone =
      this.estabelecimentoDataService
        .$estabelecimentoAtual()
        ?.timeZone?.trim() || this.timeZonePadrao;
    return DateTime.now().setZone(timeZone).isValid
      ? timeZone
      : this.timeZonePadrao;
  }
}
